import React, { useEffect, useState } from 'react'

import useIntroStore from 'hooks/useIntroStore'
import { ModalHeader } from '@ui/components/Modal'
import InputAction from '@ui/forms/Input/InputAction'
import { useQrScanner } from '@/common/CodeScanner/useQrScanner'
import { useTranslation } from 'react-i18next'
import Button from '@ui/buttons/Button'
import { Keyboard, Platform, View } from 'react-native'
import useSession, { useStore } from 'hooks/useSession'
import { requiredString } from '@ca/common'
import { FormProvider, useForm } from 'react-hook-form'
import FormInput from '@ui/forms/Input/FormInput'
import { trpc } from 'api'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import AppConfig from 'config'
import { useToast } from '@ui/toasts/useToastManager'
import { debugQR } from '@order/utils/getDebugLog'
import { z } from 'zod'
import * as Sentry from '@sentry/react-native'
import { zodResolver } from '@hookform/resolvers/zod'
import { useHeadlessStaffLoginState } from 'hooks/staff/useHeadlessStaffLogin'
import Analytics from 'hooks/analytics/Analytics'

const staffLoginFormSchema = z.object({
  username: requiredString,
  storePassword: requiredString,
  staffId: requiredString,
})

const StaffLogin = ({ onSuccess }: { onSuccess?: () => void }) => {
  const config = useRemoteConfig()
  const reset = useIntroStore((s) => s.reset)
  const setPrevScreen = useIntroStore((s) => s.setPrevScreen)
  const openQrScanner = useQrScanner((s) => s.open)
  const closeQrScanner = useQrScanner((s) => s.close)
  const isHeadlessLoading = useHeadlessStaffLoginState((s) => s.isLoading)
  const { t } = useTranslation(['auth'])
  const store = useStore()
  const qr = useIntroStore((s) => s.qr)
  const toast = useToast()

  const isValidStore = !!store
  const savedUsername = useSession((s) => s.storeLogin)
  const setStoreLogin = useSession((s) => s.setStoreLogin)

  const form = useForm({
    defaultValues: {
      staffId: AppConfig.isDev ? 'DE0221312' : '',
      username: savedUsername || '',
      storePassword: isValidStore ? 'placeholder' : '',
    },
    resolver: zodResolver(staffLoginFormSchema),
  })

  const openQrModal = () => {
    Keyboard.dismiss()
    openQrScanner()
  }

  const addQrCallback = useQrScanner((s) => s.addCallback)
  useEffect(() => {
    const unsubscribe = addQrCallback((res) => {
      debugQR.log('Qr scanned', res)
      if (!res) {
        return
      }
      form.setValue('staffId', res.code)
      // Make sure the qr modal is closed
      closeQrScanner()
    })
    return unsubscribe
  }, [])

  const [showPass, setShowPass] = useState(false)
  const toggleShowPass = () => setShowPass((prev) => !prev)

  const setStore = useSession((s) => s.setStore)
  const setStaff = useSession((s) => s.setStaff)

  const resetStore = (username?: boolean) => {
    setStoreLogin('')
    setStore(null)
    form.setValue('storePassword', '')
    if (username) {
      form.setValue('username', '')
    }
  }

  useEffect(() => {
    if (store && config && store.region !== config.region.erpCode) {
      resetStore(true)
    }
  }, [store, config])

  const staffLogin = trpc.staff.staffLogin.useMutation({
    onSuccess: (data) => {
      setStaff(data)
      Analytics.logEvent('staff_login', {
        staff_id: `${data.data.id}`,
        staff_ref: data.data.ref,
      })
      setPrevScreen(undefined)
      reset()
      onSuccess?.()
    },
    onError: (err) => {
      if (err.data?.code === 'BAD_REQUEST') {
        toast({
          text: t('auth:staff.error.invalid'),
          variant: 'error',
        })
        return
      }
      if (err.data?.code === 'UNAUTHORIZED') {
        toast({
          text: t('auth:store.error.unauthorized'),
          variant: 'error',
        })
        resetStore()
        return
      }

      Sentry.captureException(err, {
        tags: {
          location: 'StaffLogin',
          code: err.data?.code || 'unknown',
        },
        extra: {
          data: err.data,
        },
      })
      toast({
        title: t('toasts:error.default.title'),
        text: t('toasts:error.default.desc'),
        variant: 'error',
      })
    },
  })
  const storeLogin = trpc.staff.storeLogin.useMutation({
    onSuccess: (data, values) => {
      setStore(data)
      setStoreLogin(values.username)
      handleSubmit()
    },
    onError: (err) => {
      if (err.data?.httpStatus !== 401) {
        Sentry.captureException(err, {
          tags: {
            location: 'StaffLogin',
            code: err.data?.code || 'unknown',
          },
          extra: {
            data: err.data,
          },
        })
      }
      toast({
        title: t('auth:store.error.invalid'),
        text: err.message,
        variant: 'error',
      })
    },
  })

  const handleSubmit = form.handleSubmit((data) => {
    if (isValidStore) {
      staffLogin.mutate({
        ref: data.staffId,
      })
      return
    }
    storeLogin.mutate({
      username: data.username,
      password: data.storePassword,
      region: config?.region.erpCode || 'de',
    })
  })

  useEffect(() => {
    if (!qr || qr.type !== 'staff') {
      return
    }
    // Code is staff id so we can set it directly
    form.setValue('staffId', `${qr.code}`)
    if (isValidStore) {
      handleSubmit()
    }
  }, [qr, isValidStore])

  return (
    <View className="flex w-full gap-16">
      <ModalHeader path="staffLogin" ns="auth" />
      <View className="w-full items-center gap-4">
        <FormProvider {...form}>
          <FormInput
            control={form.control}
            name="staffId"
            autoCorrect={false}
            autoCapitalize="none"
            icon="tag"
            placeholder={t('auth:staffId.placeholder')}
            enterKeyHint={isValidStore ? 'done' : 'next'}
            onSubmitEditing={isValidStore ? handleSubmit : undefined}
            action={Platform.select({
              default: <InputAction icon="qr-code" onPress={openQrModal} />,
              web: null,
            })}
            next={'username'}
            keyboardAppearance="dark"
          />
          <FormInput
            control={form.control}
            name="username"
            autoCorrect={false}
            autoCapitalize="none"
            icon="user-circle"
            action={
              isValidStore ? (
                <InputAction icon="x" onPress={() => resetStore(true)} />
              ) : undefined
            }
            placeholder={t('auth:storeUsername.placeholder')}
            enterKeyHint={isValidStore ? 'done' : 'next'}
            onSubmitEditing={isValidStore ? handleSubmit : undefined}
            next={!isValidStore ? 'storePassword' : undefined}
            keyboardAppearance="dark"
          />
          <FormInput
            control={form.control}
            name="storePassword"
            autoCorrect={false}
            secureTextEntry={!showPass}
            disabled={isValidStore}
            autoCapitalize="none"
            icon="key"
            action={
              <InputAction
                icon={showPass ? 'eye-off' : 'eye'}
                onPress={toggleShowPass}
              />
            }
            onSubmitEditing={handleSubmit}
            enterKeyHint="done"
            placeholder={t('storePassword.placeholder')}
            keyboardAppearance="dark"
          />
        </FormProvider>
      </View>
      <View className="items-center">
        <Button
          onPress={handleSubmit}
          label={t('signIn')}
          iconEnd="arrow-narrow-right"
          loading={
            staffLogin.isPending || storeLogin.isPending || isHeadlessLoading
          }
        />
      </View>
    </View>
  )
}

export default StaffLogin
